/* eslint-disable @typescript-eslint/no-unused-vars */
import { QuestionCircleOutlined } from '@ant-design/icons';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Input,
  Tag,
  Tooltip
} from 'antd';
import React, { useEffect, useState } from 'react';
import {
  Controller, FormProvider, useForm
} from 'react-hook-form';
import { Label, Radio, Typography } from 'tfc-components';

import {
  IBillingInfo
} from '.';

import { addNewPackageInfoSchema } from 'utils/schemas';

type FormType = IBillingInfo;

interface BillingTypeProps {
  onSubmit: (data: FormType) => void;
  triggerSubmitRef?: React.MutableRefObject<(() => void) | null>;
  initialData?: FormType;
}

const initialInvoiceEmail = (value: string[] | undefined): string => (value ? value.map((email) => email.trim()).join(',') : '');
const BillingInformation: React.FC<BillingTypeProps> = ({
  onSubmit,
  triggerSubmitRef,
  initialData,
}) => {
  const [inputValue, setInputValue] = useState<string>(
    initialInvoiceEmail(initialData?.invoice?.invoiceEmail)
  );
  const [invoiceType, setInvoiceType] = useState<'business' | 'individual'>(initialData?.invoiceType || 'business');

  const [listOption, setListOption] = useState<string[]>([]);

  const method = useForm<FormType>({
    defaultValues: initialData || {
      invoiceType: 'business',
      invoice: {
        companyName: '',
        fullAddress: '',
        invoiceEmail: [],
        taxId: '',
      }
    },
    resolver: yupResolver(addNewPackageInfoSchema),
    mode: 'onChange'
  });

  const handleNext = async () => {
    await method.handleSubmit((data) => {
      onSubmit(data);
    })();
  };

  useEffect(() => {
    if (triggerSubmitRef) {
      // eslint-disable-next-line no-param-reassign
      triggerSubmitRef.current = handleNext;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerSubmitRef]);

  useEffect(() => {
    // method.reset({
    //   invoiceType: initialData?.invoiceType,
    //   invoice: invoiceType === 'business' ? {
    //     companyName: (initialData?.invoice as IBusinessInfo)?.companyName,
    //     fullAddress: initialData?.invoice?.fullAddress,
    //     taxId: (initialData?.invoice as IBusinessInfo)?.taxId,
    //     invoiceEmail: initialData?.invoice?.invoiceEmail
    //   } : {
    //     nationalId: (initialData?.invoice as IIndividualInfo)?.nationalId,
    //     fullName: (initialData?.invoice as IIndividualInfo)?.fullName,
    //     fullAddress: initialData?.invoice?.fullAddress,
    //     invoiceEmail: initialData?.invoice?.invoiceEmail
    //   }
    // });
  }, [initialData, invoiceType, method]);

  const invoiceEmailError = method.formState.errors?.invoice?.invoiceEmail;

  const handleSwitchType = () => {
    const newType = invoiceType === 'business' ? 'individual' : 'business';
    method.setValue('invoiceType', newType);
    setInvoiceType(newType);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if ((e.key === 'Enter' || e.key === ' ') && inputValue.trim() && !invoiceEmailError) {
      const newOptions = inputValue
        .split(',')
        .map((opt) => opt.trim())
        .filter((opt) => opt && !listOption.includes(opt));

      setListOption((prev) => [...prev, ...newOptions]);
      setInputValue('');
    }
  };

  const handleRemove = (removedOption: string) => {
    setListOption((prev) => prev.filter((opt) => opt !== removedOption));
  };

  useEffect(() => {
    const newOptions = inputValue
      .split(',')
      .map((opt) => opt.trim())
      .filter((opt) => opt && !listOption.includes(opt));

    setListOption((prev) => [...prev, ...newOptions]);
    setInputValue('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="p-checkout_billingInfo">
      <FormProvider {...method}>
        <div className="p-checkout_billingType">
          <Typography.Text fontweight="600" extendClasses="p-checkout_titleProduct p-checkout_billingInfo_spacing fs-20x24">Invoice Information</Typography.Text>
          <Label className="p-checkout_billingType_option">
            <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
              Invoice Recipient Type
              <span className="p-home_demo_quotes">*</span>
            </Typography.Text>
          </Label>
          <div className="p-checkout_billingType_options">
            <Label className="p-checkout_billingType_option">
              <Radio
                size={20}
                checked={invoiceType === 'business'}
                onChange={() => handleSwitchType()}
                name="invoiceType"
              />
              <Typography.Text extendClasses="p-checkout_billingType_optionText">
                Business
              </Typography.Text>
            </Label>
            <Label className="p-checkout_billingType_option">
              <Radio
                size={20}
                name="invoiceType"
                checked={invoiceType === 'individual'}
                onChange={() => handleSwitchType()}
              />
              <Typography.Text extendClasses="p-checkout_billingType_optionText">Individual</Typography.Text>
            </Label>
          </div>
        </div>
        <>
          <div className="p-home_demo_formLayout">
            <div className="p-home_demo_formGroup p-checkout_billingInfo_formGroup">
              <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                {invoiceType === 'business' ? 'Tax ID' : 'National ID Number'}
                {' '}
                <span className="p-home_demo_quotes">*</span>
              </Typography.Text>
              <Controller
                name="invoice.taxId"
                control={method.control}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      className="p-checkout_billingInfo_input p-home_demo_formInput"
                      onKeyPress={(e) => {
                        if (!/[0-9]/.test(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      placeholder="0312345678"
                    />
                    {(method.formState.errors as any).invoice?.taxId && (
                      <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">{(method.formState.errors as any).invoice?.taxId.message}</Typography.Text>
                    )}
                  </>
                )}
              />
            </div>
            <div className="p-home_demo_formGroup p-checkout_billingInfo_formGroup">
              <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                {invoiceType === 'business' ? 'Company Name' : 'Full Name'}
                {' '}
                <span className="p-home_demo_quotes">*</span>
              </Typography.Text>
              <Controller
                name="invoice.companyName"
                control={method.control}
                render={({ field }) => (
                  <>
                    <Input {...field} className="p-checkout_billingInfo_input p-home_demo_formInput" placeholder="Company ABC" />
                    {(method.formState.errors as any).invoice?.companyName && (
                      <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">{(method.formState.errors as any).invoice?.companyName!.message}</Typography.Text>
                    )}
                  </>
                )}
              />
            </div>
          </div>

          <div className="p-home_demo_formLayout">
            <div className="p-home_demo_formGroupRow">
              <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                Full Address
                {' '}
                <span className="p-home_demo_quotes">*</span>
                {' '}
                {
                  invoiceType === 'business'
                  && (
                    <Tooltip placement="right" title="Please enter the registered address as provided to the tax authority.">
                      <span><QuestionCircleOutlined /></span>
                    </Tooltip>
                  )
                }

              </Typography.Text>
              <Controller
                name="invoice.fullAddress"
                control={method.control}
                render={({ field }) => (
                  <>
                    <Input
                      {...field}
                      placeholder="123 Nguyen Hue St, Ben Nghe Ward, District 1, HCMC"
                      className="p-checkout_billingInfo_input p-home_demo_formInput"
                    />
                    {(method.formState.errors as any).invoice?.fullAddress && (
                      <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">
                        {(method.formState.errors as any).invoice?.fullAddress.message}
                      </Typography.Text>
                    )}
                  </>
                )}
              />
            </div>
          </div>

          <div className="p-home_demo_formLayout">
            <div className="p-home_demo_formGroupRow">
              <Typography.Text fontweight="600" extendClasses="p-checkout_billingType_formLabel p-home_demo_formLabel fs-14x18">
                Invoice Email
                {' '}
                <span className="p-home_demo_quotes">*</span>
              </Typography.Text>
              <Controller
                name="invoice.invoiceEmail"
                control={method.control}
                render={({ field }) => (
                  <>
                    <div className="p-addNewPackage_inputWrap">
                      <div className="p-addNewPackage_listTag">
                        {listOption.map((option, index) => (
                          <>
                            {index > 0
                              && (
                                <Typography.Text
                                  extendClasses="p-addNewPackage_mr10"
                                >
                                  ,
                                </Typography.Text>
                              )}
                            <Tag
                              key={option}
                              closable
                              onClose={() => handleRemove(option)}
                              className="p-addNewPackage_tag"
                            >
                              {option}
                            </Tag>
                          </>
                        ))}
                      </div>
                      <Input
                        placeholder={listOption.length === 0 ? 'email1@example.com, email2@example.com' : ''}
                        className="modalOption_textInput"
                        onKeyDown={handleKeyDown}
                        onChange={(e) => {
                          const { value } = e.target;
                          if (value !== ' ') {
                            setInputValue(value);
                            field.onChange(listOption.concat(value));
                          }
                        }}
                        value={inputValue}
                        style={{ width: '100%', padding: 8, boxSizing: 'border-box' }}
                      />

                    </div>
                    {invoiceEmailError && (
                      <Typography.Text extendClasses="p-home_demo_error p-checkout_billingInfo_errorInput">
                        {(() => {
                          if (Array.isArray(invoiceEmailError)) {
                            const firstError = invoiceEmailError[0];
                            if (firstError && typeof firstError === 'object' && 'message' in firstError) {
                              return firstError.message as string;
                            }
                            return 'Invalid email';
                          }
                          return (invoiceEmailError as any)?.message || 'Invalid email';
                        })()}
                      </Typography.Text>
                    )}
                  </>

                )}
              />
            </div>
          </div>
        </>
        <button type="submit" style={{ display: 'none' }} />
      </FormProvider>
    </div>
  );
};

export default BillingInformation;
